import React, { useState } from "react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";

import { env } from "~/env";
import { api } from "~/lib/api";
import { useAuthStore } from "~/stores/use-auth-store";
import { useDeviceStore } from "~/stores/use-device-store";

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const { impersonateId, session } = useAuthStore();

  const { deviceId } = useDeviceStore();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnMount: true,
            gcTime: 1000 * 60 * 60 * 24, // 24 hours
            networkMode: "online",
          },
          mutations: {
            retry: false,
            networkMode: "online",
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      }),
  );

  const persister = createSyncStoragePersister({
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    serialize(client) {
      return superjson.stringify(client);
    },
    deserialize(data) {
      return superjson.parse(data);
    },
  });

  const trpcClient = api.createClient({
    links: [
      httpBatchLink({
        transformer: superjson,
        url: `${env.VITE_API_URL}/trpc`,
        headers() {
          return {
            Authorization: `Bearer ${session?.access_token}`,
            "x-deployment-id": process.env.VERCEL_DEPLOYMENT_ID ?? "",
            "x-device-id": deviceId,
            "x-app-version": APP_VERSION,
            ...(impersonateId && { ["x-impersonate"]: impersonateId }),
          };
        },
      }),
    ],
  });

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <PersistQueryClientProvider
        onSuccess={() => {
          void queryClient.resumePausedMutations();
          void queryClient.invalidateQueries();
        }}
        persistOptions={{
          persister,
          maxAge: 1000 * 60 * 60 * 24,
          buster: "v5",
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              // Skip persisting queries that have 'booking' in their key
              return !query.queryKey.some(
                (key) =>
                  typeof key === "string" &&
                  key.toLowerCase().includes("booking"),
              );
            },
          },
        }}
        client={queryClient}
      >
        {children}
      </PersistQueryClientProvider>
    </api.Provider>
  );
}
